/**
 * Market Auto-Detection for Tom&Sawyer Shopify Theme
 * Detects visitor location using Shopify's browsing context suggestions API
 * Following best practices from: https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
 */

class MarketAutoDetection {
  constructor() {
    this.sessionKey = 'market-suggestion-dismissed';
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Check if we've already shown suggestion in this session
    if (this.hasDismissedThisSession()) {
      return;
    }

    // Detect user's optimal market using Shopify's API
    this.detectLocation();
  }

  hasDismissedThisSession() {
    // Check if user dismissed suggestion in this browser session
    return sessionStorage.getItem(this.sessionKey) === 'true';
  }

  setDismissedThisSession() {
    // Mark that user dismissed the suggestion for this session
    sessionStorage.setItem(this.sessionKey, 'true');
  }

  getCurrentMarket() {
    // Get current market from Shopify's global object
    return window.Shopify && window.Shopify.country ? window.Shopify.country : null;
  }

  detectLocation() {
    // Use Shopify's browsing context suggestions API (recommended method)
    const currentCountry = this.getCurrentMarket();
    const rootUrl = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

    // Build endpoint URL - exclude current country from suggestions
    let endpoint = `${rootUrl}browsing_context_suggestions.json?country[enabled]=true`;
    if (currentCountry) {
      endpoint += `&country[exclude]=${currentCountry}`;
    }

    fetch(endpoint)
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch browsing context suggestions');
        }
        return response.json();
      })
      .then(data => {
        if (data.detected_values && data.detected_values.country) {
          const detectedCountry = data.detected_values.country.handle.toUpperCase();
          this.handleLocationDetected(detectedCountry, currentCountry);
        }
      })
      .catch(error => {
        // Silently fail - don't disrupt user experience
        console.log('Market detection unavailable:', error.message);
      });
  }

  handleLocationDetected(detectedCountry, currentCountry) {
    // If detected country matches current market, no action needed
    if (detectedCountry === currentCountry) {
      return;
    }

    // Show market suggestion banner
    this.showMarketSuggestion(detectedCountry, currentCountry);
  }

  showMarketSuggestion(suggestedCountry, currentCountry) {
    // Create and show market suggestion banner
    const banner = this.createSuggestionBanner(suggestedCountry, currentCountry);
    document.body.appendChild(banner);
    
    // Animate in
    setTimeout(() => {
      banner.classList.add('market-suggestion--visible');
    }, 100);
  }

  createSuggestionBanner(suggestedCountry, currentCountry) {
    const banner = document.createElement('div');
    banner.className = 'market-suggestion';
    banner.innerHTML = `
      <div class="market-suggestion__content">
        <div class="market-suggestion__text">
          <strong>Shopping from ${this.getCountryName(suggestedCountry)}?</strong>
          <span>Switch to ${this.getCountryName(suggestedCountry)} for local pricing and shipping.</span>
        </div>
        <div class="market-suggestion__actions">
          <button class="market-suggestion__btn market-suggestion__btn--switch" data-country="${suggestedCountry}">
            Switch to ${this.getCurrencyCode(suggestedCountry)}
          </button>
          <button class="market-suggestion__btn market-suggestion__btn--dismiss">
            Stay on ${this.getCurrencyCode(currentCountry)}
          </button>
        </div>
      </div>
      <button class="market-suggestion__close" aria-label="Close">&times;</button>
    `;

    // Add event listeners
    banner.querySelector('.market-suggestion__btn--switch').addEventListener('click', () => {
      this.switchToMarket(suggestedCountry);
    });

    banner.querySelector('.market-suggestion__btn--dismiss').addEventListener('click', () => {
      this.dismissSuggestion(banner);
    });

    banner.querySelector('.market-suggestion__close').addEventListener('click', () => {
      this.dismissSuggestion(banner);
    });

    return banner;
  }

  switchToMarket(countryCode) {
    // Mark as dismissed for this session
    this.setDismissedThisSession();

    // Submit the localization form to switch markets
    const form = document.getElementById('HeaderCountryForm');
    if (form) {
      const select = form.querySelector('select[name="country_code"]');
      if (select) {
        select.value = countryCode;
        form.submit();
      }
    }
  }

  dismissSuggestion(banner) {
    // Mark as dismissed for this session only
    this.setDismissedThisSession();

    // Animate out and remove
    banner.classList.add('market-suggestion--hiding');
    setTimeout(() => {
      if (banner.parentNode) {
        banner.parentNode.removeChild(banner);
      }
    }, 300);
  }

  getCountryName(countryCode) {
    const countries = {
      'CA': 'Canada',
      'US': 'United States'
    };
    return countries[countryCode] || countryCode;
  }

  getCurrencyCode(countryCode) {
    const currencies = {
      'CA': 'CAD',
      'US': 'USD'
    };
    return currencies[countryCode] || countryCode;
  }
}

// Initialize when script loads
window.MarketAutoDetection = MarketAutoDetection;

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
  new MarketAutoDetection();
});

// Handle manual market selector changes
document.addEventListener('change', (e) => {
  if (e.target.matches('select[name="country_code"]')) {
    // User manually changed market - dismiss suggestion for this session
    sessionStorage.setItem('market-suggestion-dismissed', 'true');
  }
});